import { BinaryTree, BTTYPE } from './binary-tree';
import { CoordinatorService } from './coordinator.service';
import type { BTPAR } from './binary-tree';
import { BinaryTreeEdit } from './binary-tree-edit';
import { ADOPT_PARAMS } from './auto-user';
import { RatingConnector, TagConnector } from './calculation-classes';

export class BinaryTree$ {
    private _binaryTreeMap = new Map<string, BinaryTree>();
    // private _binaryStorageMap = new Map<string,
    //     Map<string, BinaryTreeStorage>>()  // mainKey: bt.originId, subKey: user.id

    selectedTab = {
        ratings: 0,
        tags: 0,
    };

    private _currentRatings: BinaryTree | undefined;
    private _currentTags: BinaryTree | undefined;

    constructor(private coords: CoordinatorService) {}

    clear() {
        this._binaryTreeMap.clear();
        this._currentRatings = undefined;
        this._currentTags = undefined;
        this.selectedTab = {
            ratings: 0,
            tags: 0,
        };
    }

    clearValuesCurrentUser(arg: string) {
        switch (arg) {
            case 'all':
            case 'ratings':
            case 'tags':
                this._binaryTreeMap.forEach((bt) => {
                    if (arg === 'all' || bt.type === arg) bt.reset();
                });
                break;
            default:
                const bt = this.getTree(arg);
                bt?.reset();
        }
    }

    get btMap() {
        const mp = new Map<string, Map<string, string[]>>();

        this._binaryTreeMap.forEach((bt) => {
            bt.onSetMap.forEach((itmIdSet, nodeId) => {
                itmIdSet.forEach((itmId) => {
                    let idMp = mp.get(itmId);
                    if (!idMp) {
                        idMp = new Map<string, string[]>();
                        mp.set(itmId, idMp);
                    }
                    let nodeLs = idMp.get(bt.id);
                    if (!nodeLs) {
                        nodeLs = [];
                        idMp.set(bt.id, nodeLs);
                    }
                    nodeLs.push(nodeId);
                });
            });
        });
        return mp;
    }

    typeFromId(id: string): BTTYPE | undefined {
        return this._binaryTreeMap.get(id)?.type;
    }

    adoptOmitBtIdLs(s: Set<string>, tp: BTTYPE, omitLs: any) {
        if (!omitLs) return;
        if (!Array.isArray(omitLs)) omitLs = [omitLs];
        omitLs.forEach((o: string | { id: string }) => {
            if (typeof o === 'string') {
                const bt = this.getByTitle(tp, o);
                bt && s.add(bt.id);
            } else s.add(o.id);
        });
    }

    adoptData(
        argMp: Map<string, Map<string, string[]>>,
        { tagsMode, ratingsMode, omitBtSet, itmIdSet }: ADOPT_PARAMS = {},
    ) {
        const modeMap: Map<string, string> = new Map([
            ['ratings', 'if-empty'],
            ['tags', 'if-empty'],
        ]);

        ratingsMode && modeMap.set('ratings', ratingsMode);
        tagsMode && modeMap.set('tags', tagsMode);

        this._binaryTreeMap.forEach((bt) => {
            if (omitBtSet && omitBtSet.has(bt.id)) return;
            const mode = modeMap.get(bt.type!);
            argMp.forEach((btMp, itmId) => {
                if (itmIdSet && !itmIdSet.has(itmId)) return;
                if (mode === 'replace') {
                    bt.onSetMap.forEach((s) => s.delete(itmId));
                } else if (mode === 'if-empty') {
                    for (const s of bt.onSetMap.values()) {
                        if (s.has(itmId)) return;
                    }
                }
                const ls = btMp.get(bt.id);
                ls?.forEach((ndId) => {
                    let s = bt.onSetMap.get(ndId);
                    if (!s) {
                        s = new Set<string>();
                        bt.onSetMap.set(ndId, s);
                    }
                    s.add(itmId);
                });
            });
            this.coords.display$.markForCheck();
            bt.markForManifestStore();
        });
    }

    reset(tp: BTTYPE) {
        Array.from(this._binaryTreeMap.values())
            .filter((x) => x.type == tp)
            .forEach((x) => x.reset());
    }

    setCurrentType(tp: BTTYPE, bt: BinaryTree) {
        switch (tp) {
            case 'ratings':
                this._currentRatings = bt;
                break;
            case 'tags':
                this._currentTags = bt;
                break;
        }
        this.coords.display$.markForCheck();
    }

    getCurrentType(tp: BTTYPE): BinaryTree {
        switch (tp) {
            case 'ratings':
                return this._currentRatings!;
            case 'tags':
                return this._currentTags!;
        }
    }

    editDialog(type: BTTYPE) {
        this.coords.display$.dialog$.next({
            action: type === 'ratings' ? 'ratings-editor' : 'tags-editor',
        });
    }

    get treeLs() {
        return Array.from(this._binaryTreeMap.values());
    }

    load3(ls: any[]) {
        ls.forEach((x) => {
            if (!x) return;
            try {
                const bt = new BinaryTree(this.coords, x.bt);
                this._binaryTreeMap.set(bt.id, bt);
            } catch (e) {
                this.coords.utils$.consoleError('LOAD3 BT', e);
            }
        });
    }

    load3storage(ls: any[], ownerId?: string) {
        if (!ownerId) ownerId = this.coords.user$.currentId;

        const otherLs: any[] = [];
        ls.forEach((x) => {
            try {
                const bts = x?.bts;
                if (!bts) return;
                const bt = this.getTree(bts.originId);
                if (!bt) return;

                if (x.ownerId === ownerId) bt.updateUserValues(bts.valueDct);
                else otherLs.push({ bt: bt, bts: bts.valueDct });
            } catch (e) {
                this.coords.utils$.consoleError('LOAD3 BT STORAGE', e);
            }
        });
        return {
            otherLs: otherLs,
        };
    }

    xitemRatingConnector(xitmId: string) {
        return new RatingConnector(this.coords, xitmId);
    }

    xitemTagConnector(xitmId: string) {
        return new TagConnector(this.coords, xitmId);
    }

    dialogSearch(bt: BinaryTree) {
        this.coords.display$.dialog$.next({
            action: `search-binary-tree`,
            bt: bt,
        });
    }

    tabInstance(tp: BTTYPE) {
        return this.getSortedList(tp)[this.selectedTab[tp]];
    }

    getByTitle(tp: BTTYPE, title: string) {
        title = this.coords.utils$.normalizeString(title);
        for (const bt of this._binaryTreeMap.values()) {
            if (bt.type !== tp) continue;
            if (this.coords.utils$.normalizeString(bt.title) === title) return bt;
        }
        return null;
    }

    getSortedList(tp: BTTYPE, includeOff = false) {
        const ls = Array.from(this._binaryTreeMap.values())
            .filter((x) => x.type == tp)
            .filter((v) => includeOff || v.isOn);
        ls.sort((a, b) => (a.sortTitle < b.sortTitle ? -1 : 1));

        if (!this._currentRatings && tp === 'ratings') this._currentRatings = ls[0];
        else if (!this._currentTags && tp === 'tags') this._currentTags = ls[0];
        return ls;
    }

    delete(bt: BinaryTree) {
        this._binaryTreeMap.delete(bt.id);
        if (bt === this._currentRatings) this._currentRatings = undefined;
        if (bt === this._currentTags) this._currentTags = undefined;
        this.coords.display$.markForCheck();
    }

    create(tp: BTTYPE, isTemporary = false) {
        const templ = <BTPAR>this.coords.template$.get(tp);
        const bt = this.createBinaryTree(templ, isTemporary);
        return bt;
    }

    createEditor(tp: BTTYPE) {
        return new BinaryTreeEdit(this.coords, { type: tp });
    }

    createBinaryTree(params: BTPAR = {}, isTemporary = false) {
        if (isTemporary) params.ownerId = this.coords.user$.currentId;
        const bt = new BinaryTree(this.coords, params);
        if (!isTemporary) {
            this._binaryTreeMap.set(bt.id, bt);
            bt.markForManifestStore();
        }
        return bt;
    }

    getTree(treeId: string) {
        return this._binaryTreeMap.get(treeId);
    }

    setTree(bt: BinaryTree) {
        if (!this._binaryTreeMap.has(bt.id)) {
            this._binaryTreeMap.set(bt.id, bt);
        }
    }

    get list() {
        return Array.from(this._binaryTreeMap.values());
    }

    exportJsonAll(type: BTTYPE) {
        const ls = this.getSortedList(type);
        return ls.map((x) => x.exportJsonAll());
    }

    exportJsonAllXitem(type: BTTYPE, xitemId: string) {
        const ls = this.getSortedList(type);
        return ls.map((x) => x.exportJsonAllXitem(xitemId));
    }
}
